import { displayName, lifespan, type Person } from '@lineage/core';
import { useMemo, useState } from 'react';
import { Modal } from '../../components/Modal';
import { Portrait } from '../canvas/PersonCard';
import { compareNames, fold, useLang, useT } from '../../i18n';

interface Props {
  title: string;
  people: Person[];
  onPick: (id: string) => void;
  onClose: () => void;
  /** "I'm not on the tree yet" option (Who are you). */
  onNone?: () => void;
  /** People who already belong to an account are not offered for claiming. */
  disabledIds?: Set<string>;
}

/** Searchable list of people, used for "Which one is you?", "See the tree as…" and search. */
export function PersonPicker({ title, people, onPick, onClose, onNone, disabledIds }: Props) {
  const t = useT();
  const lang = useLang();
  const [q, setQ] = useState('');
  const list = useMemo(() => {
    const needle = fold(q.trim());
    const cmp = compareNames(lang);
    return people
      .filter((p) => !needle || fold(displayName(p)).includes(needle) || fold(p.birthName ?? '').includes(needle))
      .sort((a, b) => cmp(displayName(a), displayName(b)));
  }, [people, q, lang]);
  return (
    <Modal title={title} onClose={onClose}>
      <div className="stack">
        <input className="input" type="search" autoFocus placeholder={t.common.searchByName} value={q} onChange={(e) => setQ(e.target.value)} aria-label={t.common.searchPeople} />
        <div className="stack" style={{ gap: 8 }}>
          {list.map((p) => (
            <button key={p.id} type="button" className="list-item" disabled={disabledIds?.has(p.id)} onClick={() => onPick(p.id)} style={disabledIds?.has(p.id) ? { opacity: 0.5 } : undefined}>
              <Portrait person={p} small />
              <div className="grow">
                <strong>{displayName(p)}</strong>
                <div className="muted" style={{ fontSize: 13 }}>{lifespan(p, lang)}{disabledIds?.has(p.id) ? t.picker.alreadyLinked : ''}</div>
              </div>
            </button>
          ))}
          {list.length === 0 && <p className="muted">{t.common.noMatch(q)}</p>}
        </div>
        {onNone && <button className="btn" type="button" onClick={onNone}>{t.picker.addMe}</button>}
      </div>
    </Modal>
  );
}
